// ReportingDashboard.js — v1.1
// Clickable charts with drill-down modals showing tickets behind each bar/segment.

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement,
  Title, Tooltip, Legend, Filler,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement,
  Title, Tooltip, Legend, Filler
);

// ── Config ────────────────────────────────────────────────────────────────────

const PROJECT_COLOURS = {
  DEVOPS:   "#1d9e75",
  CLOUDDEV: "#3266ad",
  ED:       "#7f77dd",
  TEMPL:    "#ef9f27",
  TEMP2A:   "#d85a30",
};
const DEV_PROJECTS = ["DEVOPS", "CLOUDDEV", "ED", "TEMPL", "TEMP2A"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function daysSince(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function mondayOfWeek(offsetWeeks) {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return new Date(d);
}

function shortDate(d) {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function proxyUrl(url) {
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const PROXY = isLocal ? "http://localhost:3001/proxy?url=" : "/api/proxy?url=";
  return PROXY + encodeURIComponent(url);
}

async function jiraSearchAll(config, jql, fields) {
  const { jiraUrl, jiraEmail, jiraToken } = config || {};
  if (!jiraUrl || !jiraEmail || !jiraToken) throw new Error("Jira not configured — check Settings");
  const auth    = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  const fieldList = (fields || ["summary","status","assignee","updated","created","project","resolutiondate","priority"]).join(",");
  let results = [], nextPageToken, isLast = false;
  while (!isLast) {
    const params = new URLSearchParams({ jql, maxResults: 100, fields: fieldList });
    if (nextPageToken) params.set("nextPageToken", nextPageToken);
    const res = await fetch(
      proxyUrl(`${baseUrl}/rest/api/3/search/jql?${params.toString()}`),
      { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
    );
    if (!res.ok) throw new Error(`Jira returned ${res.status}`);
    const data = await res.json();
    results = results.concat(data.issues || []);
    nextPageToken = data.nextPageToken || undefined;
    isLast = data.isLast !== false && !data.nextPageToken;
  }
  return results.map(i => ({
    ...i,
    browseUrl: `${baseUrl}/browse/${i.key}`,
  }));
}

// ── Drill-down Modal ──────────────────────────────────────────────────────────

function DrillModal({ title, subtitle, issues, onClose }) {
  const ref = useRef();

  // Close on outside click
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function handler(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const priorityColour = p => {
    if (!p) return "#8fa8b8";
    const l = p.toLowerCase();
    if (l === "highest" || l === "critical") return "#e24b4a";
    if (l === "high")   return "#d85a30";
    if (l === "medium") return "#ef9f27";
    return "#3266ad";
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div ref={ref} style={{
        background: "var(--surface, #1a2535)",
        border: "1px solid var(--border, #2a3a4a)",
        borderRadius: 12,
        width: "100%", maxWidth: 780,
        maxHeight: "80vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "16px 20px 12px", borderBottom: "1px solid var(--border, #2a3a4a)",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text, #e2e8f0)" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: "var(--text-muted, #8fa8b8)", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted, #8fa8b8)" }}>{issues.length} ticket{issues.length !== 1 ? "s" : ""}</span>
            <button onClick={onClose} style={{
              background: "none", border: "none", color: "var(--text-muted, #8fa8b8)",
              cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 4px",
            }}>×</button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {issues.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted, #8fa8b8)", fontSize: 13 }}>
              No tickets in this group
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border, #2a3a4a)" }}>
                  {["Key", "Summary", "Status", "Assignee", "Priority", "Updated"].map(h => (
                    <th key={h} style={{
                      padding: "8px 12px", textAlign: "left",
                      color: "var(--text-muted, #8fa8b8)", fontWeight: 500,
                      position: "sticky", top: 0,
                      background: "var(--surface, #1a2535)",
                      whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {issues.map((issue, i) => {
                  const f = issue.fields;
                  const key = issue.key;
                  const age = daysSince(f.updated);
                  return (
                    <tr key={key} style={{
                      borderBottom: "1px solid var(--border, #2a3a4a)",
                      background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                    }}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                        <a href={issue.browseUrl} target="_blank" rel="noreferrer"
                          style={{ color: "#3266ad", textDecoration: "none", fontWeight: 600 }}>
                          {key}
                        </a>
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--text, #e2e8f0)", maxWidth: 280 }}>
                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.summary}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                        <span style={{
                          background: "rgba(50,102,173,0.2)", color: "#7aabf7",
                          borderRadius: 4, padding: "2px 6px", fontSize: 11,
                        }}>{f.status?.name || "—"}</span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--text, #e2e8f0)", whiteSpace: "nowrap" }}>
                        {f.assignee?.displayName || "Unassigned"}
                      </td>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                        <span style={{ color: priorityColour(f.priority?.name) }}>
                          {f.priority?.name || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap", color: age > 14 ? "#e24b4a" : "var(--text-muted, #8fa8b8)" }}>
                        {fmtDate(f.updated)}
                        {age > 14 && <span style={{ marginLeft: 4 }}>⚠</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function ChartCard({ title, subtitle, onRefresh, controls, children }) {
  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: 10, padding: "14px 16px",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{title}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {controls}
          {onRefresh && (
            <button onClick={onRefresh} title="Refresh" style={{
              background: "none", border: "1px solid var(--border)", borderRadius: 5,
              color: "var(--text-muted)", cursor: "pointer", fontSize: 14, padding: "2px 8px",
            }}>↻</button>
          )}
        </div>
      </div>
      <span style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, display: "block" }}>{subtitle}</span>
      {children}
    </div>
  );
}

function ChartLegend({ items }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
      {items.map(({ label, color }) => (
        <span key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0, display: "inline-block" }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function ChartWrap({ loading, error, height, hint, children }) {
  return (
    <div>
      {loading && (
        <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Loading…
        </div>
      )}
      {!loading && error && (
        <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--red, #e24b4a)", fontSize: 12, textAlign: "center", padding: "0 1rem" }}>
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ position: "relative", width: "100%", height }}>{children}</div>
          {hint && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6, textAlign: "center" }}>{hint}</div>}
        </>
      )}
    </div>
  );
}

const selStyle = {
  fontSize: 11, padding: "3px 7px", borderRadius: 5,
  border: "1px solid var(--border)", background: "var(--surface2)",
  color: "var(--text)", cursor: "pointer",
};

const gridColor = "rgba(128,128,128,0.12)";
const tickColor = "#8fa8b8";
const baseOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { boxPadding: 4 } },
  scales: {
    x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 } } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 } }, beginAtZero: true },
  },
};

// ── Chart 1: Ticket Age Distribution ─────────────────────────────────────────

function TicketAgeChart({ config }) {
  const [issues, setIssues]       = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [ageField, setAgeField]   = useState("updated");
  const [modal, setModal]         = useState(null); // { title, issues }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await jiraSearchAll(
        config,
        "project = SUPPORT AND statusCategory != Done ORDER BY updated ASC",
        ["summary", "updated", "created", "status", "assignee", "priority"]
      );
      setIssues(data);

      const bucketKeys = ["0–7 days", "8–14 days", "15–30 days", "30+ days"];
      const buckets = { "0–7 days": [], "8–14 days": [], "15–30 days": [], "30+ days": [] };
      data.forEach(issue => {
        const age = daysSince(issue.fields[ageField]);
        if      (age <= 7)  buckets["0–7 days"].push(issue);
        else if (age <= 14) buckets["8–14 days"].push(issue);
        else if (age <= 30) buckets["15–30 days"].push(issue);
        else                buckets["30+ days"].push(issue);
      });

      setChartData({ buckets, bucketKeys, total: data.length });
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }, [config, ageField]);

  useEffect(() => { load(); }, [load]);

  // Re-bucket when ageField changes without re-fetching
  useEffect(() => {
    if (!issues.length) return;
    const buckets = { "0–7 days": [], "8–14 days": [], "15–30 days": [], "30+ days": [] };
    issues.forEach(issue => {
      const age = daysSince(issue.fields[ageField]);
      if      (age <= 7)  buckets["0–7 days"].push(issue);
      else if (age <= 14) buckets["8–14 days"].push(issue);
      else if (age <= 30) buckets["15–30 days"].push(issue);
      else                buckets["30+ days"].push(issue);
    });
    setChartData({ buckets, bucketKeys: ["0–7 days", "8–14 days", "15–30 days", "30+ days"], total: issues.length });
  }, [ageField, issues]);

  const colours = ["#3266ad", "#ef9f27", "#d85a30", "#a32d2d"];
  const barData = chartData ? {
    labels: chartData.bucketKeys,
    datasets: [{
      label: "Tickets",
      data: chartData.bucketKeys.map(k => chartData.buckets[k].length),
      backgroundColor: colours,
      borderWidth: 0, borderRadius: 4,
    }],
  } : null;

  const opts = {
    ...baseOpts,
    cursor: "pointer",
    onClick: (_, elements) => {
      if (!elements.length || !chartData) return;
      const label = chartData.bucketKeys[elements[0].index];
      const bucket = chartData.buckets[label];
      setModal({ title: `Ticket age — ${label}`, subtitle: `Age since ${ageField === "updated" ? "last update" : "creation"}`, issues: bucket });
    },
    plugins: {
      ...baseOpts.plugins,
      tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y} tickets (${chartData ? Math.round(ctx.parsed.y / chartData.total * 100) : 0}%) — click to view` } },
    },
  };

  return (
    <>
      <ChartCard
        title="Ticket age distribution"
        subtitle={loading ? "Loading…" : `${chartData?.total ?? 0} open SUPPORT tickets · age since ${ageField === "updated" ? "last update" : "creation"}`}
        onRefresh={load}
        controls={
          <select value={ageField} onChange={e => setAgeField(e.target.value)} style={selStyle}>
            <option value="updated">Since last update</option>
            <option value="created">Since created</option>
          </select>
        }
      >
        <ChartLegend items={chartData?.bucketKeys.map((k, i) => ({ label: k, color: colours[i] })) || []} />
        <ChartWrap loading={loading} error={error} height={210} hint="Click any bar to view tickets">
          {barData && <Bar data={barData} options={opts} style={{ cursor: "pointer" }} />}
        </ChartWrap>
      </ChartCard>
      {modal && <DrillModal title={modal.title} subtitle={modal.subtitle} issues={modal.issues} onClose={() => setModal(null)} />}
    </>
  );
}

// ── Chart 2: Weekly Throughput ────────────────────────────────────────────────

function WeeklyThroughputChart({ config }) {
  const [weekData, setWeekData]   = useState(null); // { labels, weekStarts, counts, issues }
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [weeks, setWeeks]         = useState(6);
  const [modal, setModal]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const weekStarts = [], weekLabels = [];
      for (let i = weeks - 1; i >= 0; i--) {
        const m = mondayOfWeek(i);
        weekStarts.push(m);
        weekLabels.push(shortDate(m));
      }
      const since = weekStarts[0].toISOString().split("T")[0];

      const issues = await jiraSearchAll(
        config,
        `project in (${DEV_PROJECTS.join(", ")}) AND statusCategory = Done AND resolutiondate >= "${since}" ORDER BY resolutiondate ASC`,
        ["summary", "resolutiondate", "project", "status", "assignee", "priority"]
      );

      // Map issues into week × project buckets
      const counts = {};
      const buckets = {}; // buckets[proj][weekIdx] = [issues]
      DEV_PROJECTS.forEach(p => {
        counts[p]  = new Array(weeks).fill(0);
        buckets[p] = Array.from({ length: weeks }, () => []);
      });

      issues.forEach(issue => {
        const proj = issue.fields.project?.key;
        if (!proj || !counts[proj]) return;
        const resolved = new Date(issue.fields.resolutiondate);
        for (let i = 0; i < weekStarts.length; i++) {
          const end = new Date(weekStarts[i]);
          end.setDate(end.getDate() + 7);
          if (resolved >= weekStarts[i] && resolved < end) {
            counts[proj][i]++;
            buckets[proj][i].push(issue);
            break;
          }
        }
      });

      const active = DEV_PROJECTS.filter(p => counts[p].some(n => n > 0));
      setWeekData({ weekLabels, weekStarts, counts, buckets, active });
      setChartData({
        labels: weekLabels,
        datasets: active.map(p => ({
          label: p, data: counts[p],
          backgroundColor: PROJECT_COLOURS[p] || "#888",
          borderRadius: 3, borderWidth: 0,
        })),
      });
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }, [config, weeks]);

  useEffect(() => { load(); }, [load]);

  const opts = {
    ...baseOpts,
    onClick: (_, elements) => {
      if (!elements.length || !weekData) return;
      const el = elements[0];
      const weekIdx = el.index;
      const proj    = weekData.active[el.datasetIndex];
      const ticketsInBucket = weekData.buckets[proj][weekIdx];
      const weekLabel = weekData.weekLabels[weekIdx];
      setModal({ title: `${proj} — w/c ${weekLabel}`, subtitle: `${ticketsInBucket.length} tickets completed`, issues: ticketsInBucket });
    },
    scales: {
      x: { ...baseOpts.scales.x, stacked: true, ticks: { ...baseOpts.scales.x.ticks, autoSkip: false } },
      y: { ...baseOpts.scales.y, stacked: true },
    },
    plugins: {
      ...baseOpts.plugins,
      tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y} tickets — click to view` } },
    },
  };

  const legendItems = weekData?.active?.map(p => ({ label: p, color: PROJECT_COLOURS[p] || "#888" })) || [];

  return (
    <>
      <ChartCard
        title="Weekly throughput"
        subtitle="Dev tickets completed per week (Done status)"
        onRefresh={load}
        controls={
          <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} style={selStyle}>
            <option value={4}>4 weeks</option>
            <option value={6}>6 weeks</option>
            <option value={8}>8 weeks</option>
            <option value={12}>12 weeks</option>
          </select>
        }
      >
        {legendItems.length > 0 && <ChartLegend items={legendItems} />}
        <ChartWrap loading={loading} error={error} height={210} hint="Click any bar segment to view tickets">
          {chartData && <Bar data={chartData} options={opts} style={{ cursor: "pointer" }} />}
        </ChartWrap>
      </ChartCard>
      {modal && <DrillModal title={modal.title} subtitle={modal.subtitle} issues={modal.issues} onClose={() => setModal(null)} />}
    </>
  );
}

// ── Chart 3: SDM / PC Workload ────────────────────────────────────────────────

function SdmWorkloadChart({ config }) {
  const [issueMap, setIssueMap]   = useState(null); // { members, sorted }
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [modal, setModal]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Fetch directly from Jira — all open SUPPORT tickets with assignees
      const issues = await jiraSearchAll(
        config,
        "project = SUPPORT AND statusCategory != Done ORDER BY assignee ASC",
        ["summary", "status", "assignee", "updated", "priority", "created"]
      );

      const members = {};
      issues.forEach(issue => {
        const name = issue.fields.assignee?.displayName || "Unassigned";
        if (!members[name]) members[name] = { open: [], overdue: [] };
        members[name].open.push(issue);
        if (daysSince(issue.fields.updated) > 5) {
          members[name].overdue.push(issue);
        }
      });

      const sorted = Object.entries(members)
        .filter(([name]) => name !== "Unassigned")
        .sort((a, b) => b[1].open.length - a[1].open.length)
        .slice(0, 12);

      setIssueMap({ members, sorted });

      const labels = sorted.map(([n]) => {
        const parts = n.trim().split(" ");
        return parts.length >= 2 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : n;
      });

      setChartData({
        labels,
        datasets: [
          { label: "Open",    data: sorted.map(([, v]) => v.open.length),    backgroundColor: "#3266ad", borderRadius: 3, borderWidth: 0 },
          { label: "Overdue", data: sorted.map(([, v]) => v.overdue.length), backgroundColor: "#e24b4a", borderRadius: 3, borderWidth: 0 },
        ],
      });
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }, [config]);

  useEffect(() => { load(); }, [load]);

  const barCount = chartData?.labels?.length || 6;

  const opts = {
    ...baseOpts,
    indexAxis: "y",
    onClick: (_, elements) => {
      if (!elements.length || !issueMap) return;
      const el        = elements[0];
      const personIdx = el.index;
      const isOverdue = el.datasetIndex === 1;
      const [name, data] = issueMap.sorted[personIdx];
      const ticketsToShow = isOverdue ? data.overdue : data.open;
      const shortName = chartData.labels[personIdx];
      setModal({
        title: `${shortName} — ${isOverdue ? "Overdue" : "Open"} tickets`,
        subtitle: `${ticketsToShow.length} ticket${ticketsToShow.length !== 1 ? "s" : ""}`,
        issues: ticketsToShow,
      });
    },
    scales: {
      x: { ...baseOpts.scales.x, stacked: true },
      y: { ...baseOpts.scales.y, stacked: true },
    },
    plugins: {
      ...baseOpts.plugins,
      tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.x} tickets — click to view` } },
    },
  };

  return (
    <>
      <ChartCard
        title="SDM / PC workload"
        subtitle="Open SUPPORT tickets per assignee — fetched live from Jira"
        onRefresh={load}
      >
        <ChartLegend items={[
          { label: "Open",    color: "#3266ad" },
          { label: "Overdue (>5 days no update)", color: "#e24b4a" },
        ]} />
        <ChartWrap loading={loading} error={error} height={Math.max(180, barCount * 36 + 40)} hint="Click any bar to view tickets">
          {chartData && <Bar data={chartData} options={opts} style={{ cursor: "pointer" }} />}
        </ChartWrap>
      </ChartCard>
      {modal && <DrillModal title={modal.title} subtitle={modal.subtitle} issues={modal.issues} onClose={() => setModal(null)} />}
    </>
  );
}

// ── Metric Cards ──────────────────────────────────────────────────────────────

function MetricCards({ tasks }) {
  const openSupport = tasks.filter(t => t.source === "jira" && !t.done && t.jiraKey?.startsWith("SUPPORT-")).length;
  const slaBreaches = tasks.filter(t => t.source === "jira" && !t.done && (t.slaBreach || t.slaBreached)).length;
  const closedWeek  = tasks.filter(t => t.done && daysSince(t.completedAt || t.jiraResolutionDate) <= 7).length;
  const noReply     = tasks.filter(t => t.source === "jira" && !t.done && t.noReply).length;

  const items = [
    { label: "Open SUPPORT tickets", value: openSupport },
    { label: "SLA breaches",         value: slaBreaches, warn: slaBreaches > 0 },
    { label: "Closed this week",     value: closedWeek },
    { label: "No-reply",             value: noReply,     warn: noReply > 0 },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
      {items.map(m => (
        <div key={m.label} style={{ background: "var(--surface2)", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{m.label}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: m.warn ? "var(--red, #e24b4a)" : "var(--text)" }}>{m.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function ReportingDashboard({ config, tasks }) {
  return (
    <div style={{ padding: "20px 0", maxWidth: 1100 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>Reporting</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Live data · all charts fetch directly from Jira · click any bar to drill down</div>
      </div>

      <MetricCards tasks={tasks || []} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14, marginBottom: 14 }}>
        <TicketAgeChart config={config} />
        <WeeklyThroughputChart config={config} />
      </div>

      <SdmWorkloadChart config={config} />
    </div>
  );
}
